/**
 * Authentication Controller
 * Main controller that orchestrates authentication flows based on environment
 */

import { EnvironmentType } from "../utils/environmentDetection";
import { handleWopiAuth, isWopiReady, initializeWopi } from "./wopiAuth";
import { handleSharePointAuth, isSharePointReady, initializeSharePoint } from "./sharepointAuth"; 
import { handleDesktopAuth, isDesktopReady, initializeDesktop } from "./desktopAuth";

/**
 * Main authentication handler that delegates to environment-specific modules
 * @param {Object} environment - Environment detection result
 * @returns {Object} - Authentication result
 */
export const handleAuthentication = async (environment) => {
  console.log("[AUTH-CONTROLLER] Handling authentication for environment:", environment.type);
  
  switch (environment.type) {
    case EnvironmentType.BROWSER:
      console.warn("[AUTH-CONTROLLER] Browser context detected - not an Office host");
      return {
        success: false,
        component: 'BrowserNotSupported',
        message: 'This add-in must be opened from Microsoft Word'
      };
    case EnvironmentType.WOPI:
      if (!isWopiReady()) {
        console.warn("[AUTH-CONTROLLER] WOPI environment not ready");
        return {
          success: false,
          component: 'ServiceUnavailable',
          message: 'WOPI environment not ready'
        };
      }
      initializeWopi();
      return await handleWopiAuth();
      
    case EnvironmentType.SHAREPOINT:
      if (!isSharePointReady()) {
        console.warn("[AUTH-CONTROLLER] SharePoint environment not ready");
        return {
          success: false,
          component: 'ServiceUnavailable',
          message: 'SharePoint environment not ready'
        };
      }
      initializeSharePoint();
      return await handleSharePointAuth();
      
    case EnvironmentType.DESKTOP:
      if (!isDesktopReady()) {
        console.warn("[AUTH-CONTROLLER] Desktop environment not ready");
        return {
          success: false,
          component: 'ServiceUnavailable',
          message: 'Desktop environment not ready'
        };
      }
      initializeDesktop();
      return await handleDesktopAuth();
      
    default:
      console.error("[AUTH-CONTROLLER] Unknown environment type:", environment.type);
      return {
        success: false,
        component: 'ServiceUnavailable',
        message: 'Unknown environment type'
      };
  }
};

/**
 * Get the appropriate component name based on authentication result
 * @param {Object} authResult - Authentication result
 * @returns {string} - Component name to render
 */
export const getComponentForAuthResult = (authResult) => {
  return authResult.component || 'ServiceUnavailable';
};

/**
 * Check if any environment is ready for authentication
 * @param {Object} environment - Environment detection result
 * @returns {boolean} - Whether environment is ready
 */
export const isEnvironmentReady = (environment) => {
  switch (environment.type) {
    case EnvironmentType.WOPI:
      return isWopiReady();
    case EnvironmentType.SHAREPOINT:
      return isSharePointReady();
    case EnvironmentType.DESKTOP:
      return isDesktopReady();
    default:
      return false;
  }
};

export default {
  handleAuthentication,
  getComponentForAuthResult,
  isEnvironmentReady
};
